import React from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@mui/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  TableHead,
  Paper,
  Button,
  IconButton,
} from "@mui/material";
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import CircularProgressWithLabel from "./CircularProgressWithLabel";

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const useStyles2 = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  gridRow: {
    marginBottom: theme.spacing(3),
  },
  table: {
    minWidth: 500,
  },
}));

function DataTable({
  source,
  tableData,
  tableFields,
  logFields,
  logs,
  schedule,
  deleteFunc,
  type,
  inner,
  setRefresh,
}) {
  const classes = useStyles2();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, tableData.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          size="small"
          aria-label="custom pagination table"
        >
          <TableHead>
            <TableRow>
              {/* IF FROM LOGS PAGE SET LOGFIELDS OR TABLEFIELDS */}
              {logs ? (
                logFields.map((value, index) => {
                  return (
                    <TableCell key={index}>
                      <b>{value.name}</b>
                    </TableCell>
                  );
                })
              ) : schedule ? null : (
                <>
                  {tableFields.map((value, index) => {
                    return (
                      <TableCell key={index}>
                        <b>{value.name}</b>
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <b className="text-danger">Stop Job</b>
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? tableData.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
              : tableData
            ).map((row) => (
              <TableRow key={row["id"]}>
                {logs ? (
                  logFields.map((value, index) => {
                    return resolveType(value, index, row);
                  })
                ) : (
                  <>
                    {tableFields.map((value, index) => {
                      return resolveType(value, index, row);
                    })}
                    {schedule ? null : (
                      <TableCell style={{ width: 160 }}>
                        {row["status"] && (
                          <Button
                            variant="outlined"
                            className="text-danger border-danger border"
                            onClick={() => {
                              deleteFunc(
                                "ABCD12345",
                                source.key,
                                row["id"],
                                type,
                                inner
                              );
                              setRefresh((prevValue) => !prevValue);
                            }}
                          >
                            STOP
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </>
                )}
              </TableRow>
            ))}

            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[1, 5, 10]}
                colSpan={3}
                count={tableData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { "aria-label": "rows per page" },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </div>
  );
}

// Resolving various Types in Table
const resolveType = (value, index, row) => {
  switch (value.type) {
    case "Progress":
      return (
        <TableCell style={{ width: 160 }} key={index}>
          <CircularProgressWithLabel value={row[value.name]} />
        </TableCell>
      );

    case "Label":
      const labelValues = {
        0: {
          className: "warning",
          value: "Currently Executing",
        },
        1: {
          className: "success",
          value: "Completed",
        },
        2: {
          className: "danger",
          value: "Failed",
        },
      };
      return (
        <TableCell
          style={{ width: 160 }}
          key={index}
          className={`text-${labelValues[row[value.name]]["className"]}`}
        >
          {labelValues[row[value.name]]["value"]}
        </TableCell>
      );

    case "Options":
      let options = "NA";
      if (row[value.name] || value["default"]) {
        options = row[value.options]
          .map((obj) => {
            return obj.name;
          })
          .join();
      }

      return (
        <TableCell style={{ width: 160 }} key={index}>
          {options}
        </TableCell>
      );

    case "SingleOption":
      return (
        <TableCell style={{ width: 160 }} key={index}>
          {row[[value.name]]["name"]}
        </TableCell>
      );

    case "Date":
      return (
        <TableCell style={{ width: 160 }} key={index}>
          {new Date(row[value.name]).toLocaleString()}
        </TableCell>
      );

    //FOR STRING AND ID
    default:
      return (
        <TableCell style={{ width: 160 }} key={index}>
          {row[value.name]}
        </TableCell>
      );
  }
};

export default DataTable;
